import styles from './table.module.scss';
import { useTranslation } from 'react-i18next';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { TableCell, TableSortLabel } from '@mui/material';
import { useEffect, useState } from 'react';
import { formatToMaxFourDigits, IconWrapper } from '@flexo/general';
import {
  transformRealDataToStructuredData,
  sortRows,
  createGetCellContent,
  Collapsible,
} from './helpers';
import { useSelector } from 'react-redux';

/**
 * PaymentsTable component displays payments data grouped by month
 * Each month shows a list of sites with their payment information
 */
export function PaymentsTable(props: any) {
  // Extract CSS classes
  const { Table__NO_DATA } = styles;
  const metadata = useSelector((state: any) => state.metadata);
  const kpi = useSelector((state: any) => state.kpi);

  // Translations and data
  const { t } = useTranslation();
  const { title, no_data_text, rows } = t(`widgets.tables.paymentsTable`, {
    returnObjects: true,
  }) as any;

  // State for sorting
  const [rowsOrder, setRowsOrder] = useState<[string, string]>(['site', 'asc']);
  const [storedRows, setStoredRows] = useState(
    transformRealDataToStructuredData([], metadata)
  );

  const [isExpanded, setIsExpanded] = useState(
    Array.from({ length: storedRows?.length }, () => true).reduce(
      (acc: any, curr: any, index: number) => {
        acc[`Q${index + 1}`] = curr;
        return acc;
      },
      {}
    )
  );

  /**
   * Handle sorting using the helper function
   * @param columnId - The column ID to sort by
   */
  function handleSort(columnId: string) {
    const newRows = sortRows(storedRows, columnId, rowsOrder, setRowsOrder);
    setStoredRows(newRows);
  }

  // Use the factory function to create a getCellContent with styling from styles
  const getCellContent = createGetCellContent(t, true);

  useEffect(() => {

    if (kpi?.kwickInvoices?.length === 0 || !kpi?.kwickInvoices) {
      return;
    }

    setStoredRows(transformRealDataToStructuredData(kpi?.kwickInvoices, metadata) as any);

    
    
  }, [kpi?.kwickInvoices?.length]);

  useEffect(() => {

    if (storedRows?.length === 0) {
      return;
    }

    setIsExpanded(
      Array.from({ length: storedRows?.length }, () => true).reduce(
        (acc: any, curr: any, index: number) => {
          acc[`Q${index + 1}`] = curr;
          return acc;
        },
        {}
      )
    )
  }, [storedRows?.length]);

  return (
    <>
      { props?.showTable !== false && storedRows?.length > 0 && isExpanded ? (
        <div>
          <div className={`${styles.Table__Title}`}>{title}</div>
          <div className={ `${styles.Table__OverflowContainer} ${styles.Table__Wider}` }>

          <div
            className={`${styles.Table} ${styles.Table__Flex} ${styles.Table__Payments}`}
          >
            <TableContainer component={Paper} key={`payments_table_header`}>
              <Table aria-label="simple table">
                <TableHead key={`payments_table_head_header`}>
                  <TableRow>
                    {rows &&
                      rows.map((column: any, column_i: number) => (
                        <TableCell
                          className={`
                            ${
                              column.sortable
                                ? styles.Table__Header__Cell__Sortable
                                : styles.Table__Header__Cell__Static
                            }
                            ${
                              column.sortable && rowsOrder[0] === column.field
                                ? styles.Table__Header__Cell__Active
                                : styles.Table__Header__Cell
                            }`}
                          key={`site_payments_table_${column_i}`}
                        >
                          <TableSortLabel
                            active={
                              column.sortable && rowsOrder[0] === column.field
                            }
                            direction={
                              rowsOrder[0] === column.field &&
                              rowsOrder[1] === 'asc'
                                ? 'asc'
                                : 'desc'
                            }
                            onClick={() =>
                              column.sortable ? handleSort(column.field) : null
                            }
                            className={`${styles.Table__Header__Cell}`}
                            IconComponent={() =>
                              rowsOrder[0] === column.field ? (
                                rowsOrder[1] === 'asc' ? (
                                  <IconWrapper
                                    iconName="chevron-up"
                                    className={`${styles.Table__TH__Icon}`}
                                  />
                                ) : (
                                  <IconWrapper
                                    iconName="chevron-down"
                                    className={`${styles.Table__TH__Icon}`}
                                  />
                                )
                              ) : (
                                <div style={{ height: 32, width: 19 }}></div>
                              )
                            }
                          >
                            <div>{column.title}</div>
                          </TableSortLabel>
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
              </Table>
            </TableContainer>
          </div>

          {storedRows?.map((month: any, month_i: number) => (
            <div
              key={`${month.month}_table_container_header_controller_${month_i}`}
              className={`${styles.Table} ${styles.Table__Flex} ${styles.Table__Payments} `}
            >
              <TableContainer
                component={Paper}
                key={`${month.month}_table_container`}
                className={`${styles.Table__Payments__Collapser} ${isExpanded[`Q${month_i + 1}`] ? styles.Table__Payments__Collapser__Expanded : styles.Table__Payments__Collapser__Collapsed}`}
                onClick={() =>
                  setIsExpanded((prev: any) => ({
                    ...prev,
                    [`Q${month_i + 1}`]: !prev[`Q${month_i + 1}`],
                  }))
                }
              >
                <Table aria-label="simple table">
                  <TableHead
                    key={`${month.month}_table_head_controller`}
                    
                  >
                    <TableRow>
                      <TableCell
                        className={styles.Table__Payments__Cell__Month}
                      >
                        {month.month}
                      </TableCell>
                      <TableCell></TableCell>
                      <TableCell>
                        {month.sites[0].unit}{' '}
                        {formatToMaxFourDigits(
                          month.sites.reduce(
                            (acc: any, site: any) => acc + site.amount,
                            0
                          )
                        )}
                      </TableCell>
                      <TableCell>
                        {(month?.sites || []).reduce(
                          (acc: any, site: any) => acc + (site.paid ? 1 : 0),
                          0
                        )}
                        {'/'}
                        {month.sites?.length || 0}{' '}
                        {t(`widgets.tables.paymentsTable.paidInvoices`)}
                      </TableCell>
                      <IconWrapper
                        iconName="chevron-down"
                      />
                    </TableRow>
                  </TableHead>
                </Table>
              </TableContainer>

              <Collapsible key={`${month.month}_collapsible`} open={isExpanded[`Q${month_i + 1}`]}>
                <TableContainer component={Paper} key={`${month.month}_table_container_body`}>
                  <Table aria-label="simple table">
                    <TableBody>
                      {month.sites.map((site, siteIndex) => (
                        <TableRow
                          key={`${month.month}_site_${siteIndex}`}
                          sx={{
                            '&:last-child td, &:last-child th': { border: 0 },
                          }}
                          style={{ height: 96 }}
                        >
                          {rows.map((column: any, columnIndex: number) => (
                            <TableCell
                              key={`${month.month}_site_${siteIndex}_col_${columnIndex}`}
                              className={`${
                                !rows?.[columnIndex]?.sortable === true &&
                                styles.Table__Cell__Static
                              }`}
                              component="th"
                              scope="row"
                            >
                              <div>{getCellContent(column, site)}</div>
                            </TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapsible>
            </div>
          ))}
            </div>
        </div>
      ) : (
        <div
          className={`${styles.Table} mb-3`}
          key={Math.floor(Math.random() * 100)}
        >
          <div className={`${styles.Table__Title} heading4`}>{title}</div>
          <div className={`${Table__NO_DATA}`}>{no_data_text}</div>
        </div>
      )}
    </>
  );
}

export default PaymentsTable;
